import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import useAuth from '../hooks/useAuth';

// Layout exclusive to /crm-view — same pages/permissions as the regular sidebar Layout, but a
// top navigation bar instead of a left sidebar. Built for embedding inside the Chatwoot
// "Relatórios > Campanhas" iframe (see reports-metaads backend GET /api/auth/sso/:token and
// Chatwoot's CampaignsReports.vue), where a fixed-width sidebar wastes horizontal space and
// makes the embedded app feel cramped. Not linked from the regular app — reached only via the
// SSO handoff (SsoLogin.jsx redirects here) or by navigating to /crm-view directly.
const NAV_ITEMS = [
  { path: '/', icon: 'dashboard', label: 'Dashboard' },
  { path: '/accounts', icon: 'hub', label: 'Contas Meta' },
  { path: '/reports', icon: 'insert_drive_file', label: 'Relatórios Meta' },
  { path: '/page-reports', icon: 'insights', label: 'Relatórios Páginas' },
  { path: '/franchise-reports', icon: 'storefront', label: 'Relatório Franquia' },
  { path: '/google-ads-reports', icon: 'ads_click', label: 'Relatórios Google Ads' },
  { path: '/combined-reports', icon: 'merge', label: 'Relatórios Combinados' },
  { path: '/clinicorp-reports', icon: 'local_hospital', label: 'Clinicorp' },
  { path: '/franchise-v2', icon: 'merge_type', label: 'Franquia V2' },
  { path: '/leads', icon: 'group', label: 'Leads' },
  { path: '/page-subscriptions', icon: 'rss_feed', label: 'Inscrição Páginas', adminOnly: true },
  { path: '/lead-source-mappings', icon: 'alt_route', label: 'Mapeamento Domínios', adminOnly: true },
  { path: '/conversions', icon: 'track_changes', label: 'Conversões' },
  { path: '/forms', icon: 'dynamic_form', label: 'Formulários' },
  { path: '/clinic-data', icon: 'medical_information', label: 'Dados Clínicas' },
  { path: '/clinic-data/reports', icon: 'summarize', label: 'Relatórios' },
  { path: '/settings', icon: 'settings', label: 'Configurações' },
];

const TopNavLayout = () => {
  const location = useLocation();
  const { logout, user } = useAuth();

  // Route paths under /crm-view are prefixed (e.g. /crm-view/accounts), but access grants in
  // user.accessibleReports use the bare path (e.g. /accounts) — same convention as Layout.jsx.
  const bareCurrentPath = location.pathname.replace(/^\/crm-view/, '') || '/';
  const isActive = (path) => bareCurrentPath === path;

  const hasAccess = (path, adminOnly) => {
    if (!user) return false;
    if (user.roleType === 'ADMIN') return true;
    if (adminOnly) return false;
    const basePaths = ['/', '/accounts', '/leads', '/settings'];
    if (basePaths.includes(path)) return true;
    // Reuses the '/clinic-data' tab grant — no separate permission for the reports sub-page.
    const permissionPath = path === '/clinic-data/reports' ? '/clinic-data' : path;
    return !!user.accessibleReports?.includes(permissionPath);
  };

  return (
    <div className="min-h-screen flex flex-col bg-surface font-body text-on-surface">
      <header className="sticky top-0 z-50 bg-sidebar border-b border-sidebar-border">
        <div className="flex items-center justify-between px-4 md:px-8 py-3 gap-4">
          <Link to="/crm-view" className="shrink-0">
            <img src="/LOGO_dark.png" alt="Lumina Reporting" className="h-8 w-auto" />
          </Link>

          <nav className="flex-1 flex items-center gap-1 overflow-x-auto">
            {NAV_ITEMS.filter((item) => hasAccess(item.path, item.adminOnly)).map((item) => (
              <Link
                key={item.path}
                to={`/crm-view${item.path === '/' ? '' : item.path}`}
                className={`topnav-item ${isActive(item.path) ? 'topnav-item-active' : 'topnav-item-inactive'}`}
              >
                <span className="material-symbols-outlined shrink-0 text-[18px]" data-icon={item.icon}>{item.icon}</span>
                <span className="font-label uppercase tracking-wide text-[9px] leading-tight whitespace-nowrap">{item.label}</span>
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            {user && (
              <div className="hidden md:flex items-center gap-2 px-2">
                <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold">
                  {user?.username?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <p className="text-[10px] font-bold text-white truncate max-w-[120px]">{user?.username || 'Usuário'}</p>
              </div>
            )}
            <button
              onClick={logout}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sidebar-text font-medium hover:text-red-400 hover:bg-sidebar-hover transition-colors duration-200"
              title="Sair"
            >
              <span className="material-symbols-outlined shrink-0 text-[18px]" data-icon="logout">logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 md:px-8 py-8 max-w-[1400px] mx-auto w-full">
        <Outlet />
      </main>
    </div>
  );
};

export default TopNavLayout;
